import { QuestionCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import classNames from 'classnames'
import { observer } from 'mobx-react'
import style from './ModelTip.module.less'

export const BaseModelTip = observer((props: { styleCSS?: React.CSSProperties }) => {
  const baseModelTip = (
    <div>
      <p className={classNames(style.title)}>基础模型</p>
      <p className={classNames(style.desc)}>包含微软翻译、智谱GLM-4-Flash，可以免费无限次使用</p>
    </div>
  )

  return (
    <Tooltip
      title={baseModelTip}
      overlayStyle={{
        maxWidth: '300px',
      }}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={props.styleCSS} />
    </Tooltip>
  )
})

interface BaseAdvancedModelTipProps {
  styleCSS?: React.CSSProperties
}

interface AdvancedModelTipPropsWithToken extends BaseAdvancedModelTipProps {
  showToken: true
  dailyFreeCount: number
  dailyFreeCountTotal: number
  vipTokenCount: number
  vipTokenTotalCount: number
  packageTokenCount: number
  packageTokenTotalCount: number
}

interface AdvancedModelTipPropsWithoutToken extends BaseAdvancedModelTipProps {
  showToken: false
}

type AdvancedModelTipProps = AdvancedModelTipPropsWithToken | AdvancedModelTipPropsWithoutToken

export const AdvancedModelTip = observer((props: AdvancedModelTipProps) => {
  const { styleCSS, showToken } = props

  // 使用类型守卫来安全地访问token相关字段
  const tokenData = showToken
    ? {
        dailyFreeCount: props.dailyFreeCount,
        dailyFreeCountTotal: props.dailyFreeCountTotal,
        vipTokenCount: props.vipTokenCount,
        vipTokenTotalCount: props.vipTokenTotalCount,
        packageTokenCount: props.packageTokenCount,
        packageTokenTotalCount: props.packageTokenTotalCount,
      }
    : null

  const advancedModelTip = (
    <div>
      <div className={classNames(style.title)}>
        高级模型
        {showToken && tokenData && (
          <p className={classNames(style.total)}>
            剩余：
            {(
              tokenData.vipTokenCount +
              tokenData.dailyFreeCount +
              tokenData.packageTokenCount
            ).toLocaleString()}
            /
            {(
              tokenData.vipTokenTotalCount +
              tokenData.dailyFreeCountTotal +
              tokenData.packageTokenTotalCount
            ).toLocaleString()}
          </p>
        )}
      </div>
      <p className={classNames(style.desc)}>
        包含 DeepL（经典）、DeepL（新一代）、DeepSeek V3.2
        Exp、Doubao-Seed-1.6、Qwen-3-Plus、智谱GLM-4.5、GPT-4.1 mini、Claude 3.5 Haiku、Gemini 2.5
        Flash
      </p>
      <p className={classNames(style.small)}>
        注：GPT-4.1 mini、Claude 3.5 Haiku、Gemini 2.5 Flash，仅限翻译场景使用
      </p>

      {showToken && tokenData && (
        <div className={classNames(style.token)}>
          <p>额度包含:</p>
          <p>
            每日赠送额度：{tokenData.dailyFreeCount.toLocaleString()}/
            {tokenData.dailyFreeCountTotal.toLocaleString()}
          </p>
          <p>
            套餐额度：{tokenData.vipTokenCount.toLocaleString()}/
            {tokenData.vipTokenTotalCount.toLocaleString()}
          </p>
          <p>
            加量包额度：{tokenData.packageTokenCount.toLocaleString()}/
            {tokenData.packageTokenTotalCount.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  )

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: '300px',
      }}
      title={advancedModelTip}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={styleCSS} />
    </Tooltip>
  )
})

type UltraModelTipProps = BaseAdvancedModelTipProps & {
  showToken: true
  dailyFreeCount: number
  dailyFreeCountTotal: number
  vipTokenCount: number
  vipTokenTotalCount: number
  packageTokenCount: number
  packageTokenTotalCount: number
}

export const UltraModelTip = observer((props: UltraModelTipProps) => {
  const { styleCSS, showToken } = props

  // 使用类型守卫来安全地访问token相关字段
  const tokenData = showToken
    ? {
        dailyFreeCount: props.dailyFreeCount,
        dailyFreeCountTotal: props.dailyFreeCountTotal,
        vipTokenCount: props.vipTokenCount,
        vipTokenTotalCount: props.vipTokenTotalCount,
        packageTokenCount: props.packageTokenCount,
        packageTokenTotalCount: props.packageTokenTotalCount,
      }
    : null

  const advancedModelTip = (
    <div>
      <div className={classNames(style.title)}>
        顶级模型
        {showToken && tokenData && (
          <p className={classNames(style.total)}>
            剩余：
            {(
              tokenData.vipTokenCount +
              tokenData.dailyFreeCount +
              tokenData.packageTokenCount
            ).toLocaleString()}
            /
            {(
              tokenData.vipTokenTotalCount +
              tokenData.dailyFreeCountTotal +
              tokenData.packageTokenTotalCount
            ).toLocaleString()}
          </p>
        )}
      </div>
      <p className={classNames(style.desc)}>包含Claude 4 Sonnet、GPT-5、Gemini 2.5 Pro</p>
      <p className={classNames(style.small)}>
        注：Claude 4 Sonnet、GPT-5、Gemini 2.5 Pro，仅限翻译场景使用
      </p>

      {showToken && tokenData && (
        <div className={classNames(style.token)}>
          <p>额度包含:</p>
          <p>
            每日赠送额度：{tokenData.dailyFreeCount.toLocaleString()}/
            {tokenData.dailyFreeCountTotal.toLocaleString()}
          </p>
          <p>
            套餐额度：{tokenData.vipTokenCount.toLocaleString()}/
            {tokenData.vipTokenTotalCount.toLocaleString()}
          </p>
          <p>
            加量包额度：{tokenData.packageTokenCount.toLocaleString()}/
            {tokenData.packageTokenTotalCount.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  )

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: '300px',
      }}
      title={advancedModelTip}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={styleCSS} />
    </Tooltip>
  )
})

// 分段翻译

interface BasePdfProTipProps {
  styleCSS?: React.CSSProperties
}

interface PdfProTipPropsWithToken extends BasePdfProTipProps {
  showToken: true
  dailyFreeCount: number
  dailyFreeCountTotal: number
  vipCount: number
  vipTotalCount: number
  packageCount: number
  packageCountTotal: number
}

interface PdfProTipPropsWithoutToken extends BasePdfProTipProps {
  showToken: false
}

type PdfProTipProps = PdfProTipPropsWithToken | PdfProTipPropsWithoutToken

export const PdfProTip = observer((props: PdfProTipProps) => {
  const { styleCSS, showToken } = props

  // 使用类型守卫来安全地访问token相关字段
  const tokenData = showToken
    ? {
        dailyFreeCount: props.dailyFreeCount,
        dailyFreeCountTotal: props.dailyFreeCountTotal,
        vipCount: props.vipCount,
        vipTotalCount: props.vipTotalCount,
        packageCount: props.packageCount,
        packageCountTotal: props.packageCountTotal,
      }
    : null

  const pdfProTip = (
    <div>
      <div className={classNames(style.title)}>
        分段翻译
        {showToken && tokenData && (
          <p className={classNames(style.total)}>
            剩余：
            {(
              tokenData.vipCount +
              tokenData.dailyFreeCount +
              tokenData.packageCount
            ).toLocaleString()}
            /
            {(
              tokenData.vipTotalCount +
              tokenData.dailyFreeCountTotal +
              tokenData.packageCountTotal
            ).toLocaleString()}
          </p>
        )}
      </div>

      {showToken && tokenData && (
        <div className={classNames(style.token)}>
          <p>额度包含:</p>
          <p>
            每日赠送额度：{tokenData.dailyFreeCount.toLocaleString()}/
            {tokenData.dailyFreeCountTotal.toLocaleString()}
          </p>
          <p>
            套餐额度：{tokenData.vipCount.toLocaleString()}/
            {tokenData.vipTotalCount.toLocaleString()}
          </p>
          <p>
            加量包额度：{tokenData.packageCount.toLocaleString()}/
            {tokenData.packageCountTotal.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  )

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: '300px',
      }}
      title={pdfProTip}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={styleCSS} />
    </Tooltip>
  )
})

export const PdfUltraTip = observer((props: PdfProTipProps) => {
  const { styleCSS, showToken } = props

  // 使用类型守卫来安全地访问token相关字段
  const tokenData = showToken
    ? {
        dailyFreeCount: props.dailyFreeCount,
        dailyFreeCountTotal: props.dailyFreeCountTotal,
        vipCount: props.vipCount,
        vipTotalCount: props.vipTotalCount,
        packageCount: props.packageCount,
        packageCountTotal: props.packageCountTotal,
      }
    : null

  const pdfProTip = (
    <div>
      <div className={classNames(style.title)}>
        无损翻译
        {showToken && tokenData && (
          <p className={classNames(style.total)}>
            剩余：
            {(
              tokenData.vipCount +
              tokenData.dailyFreeCount +
              tokenData.packageCount
            ).toLocaleString()}
            /
            {(
              tokenData.vipTotalCount +
              tokenData.dailyFreeCountTotal +
              tokenData.packageCountTotal
            ).toLocaleString()}
          </p>
        )}
      </div>

      {showToken && tokenData && (
        <div className={classNames(style.token)}>
          <p>额度包含:</p>
          <p>
            每日赠送额度：{tokenData.dailyFreeCount.toLocaleString()}/
            {tokenData.dailyFreeCountTotal.toLocaleString()}
          </p>
          <p>
            套餐额度：{tokenData.vipCount.toLocaleString()}/
            {tokenData.vipTotalCount.toLocaleString()}
          </p>
          <p>
            加量包额度：{tokenData.packageCount.toLocaleString()}/
            {tokenData.packageCountTotal.toLocaleString()}
          </p>
        </div>
      )}
    </div>
  )

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: '300px',
      }}
      title={pdfProTip}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={styleCSS} />
    </Tooltip>
  )
})

// 图片翻译

interface BaseImageTipProps {
  styleCSS?: React.CSSProperties
}

interface ImageTipPropsWithToken extends BaseImageTipProps {
  showToken: true
  dailyFreeCount: number
  dailyFreeCountTotal: number
  packageCount: number
  packageCountTotal: number
}

interface ImageTipPropsWithoutToken extends BaseImageTipProps {
  showToken: false
}

type ImageTipProps = ImageTipPropsWithToken | ImageTipPropsWithoutToken

export const ImageTip = observer((props: ImageTipProps) => {
  const { styleCSS, showToken } = props

  // 使用类型守卫来安全地访问token相关字段
  const tokenData = showToken
    ? {
        dailyFreeCount: props.dailyFreeCount,
        dailyFreeCountTotal: props.dailyFreeCountTotal,
        packageCount: props.packageCount,
        packageCountTotal: props.packageCountTotal,
      }
    : null

  const pdfProTip = (
    <div>
      <div className={classNames(style.title)}>
        图片翻译
        {showToken && tokenData && (
          <p className={classNames(style.total)}>
            剩余：{tokenData.packageCount + tokenData.dailyFreeCount}/
            {tokenData.packageCountTotal + tokenData.dailyFreeCountTotal}
          </p>
        )}
      </div>

      <p className={classNames(style.desc)}>图片翻译和截图翻译共用额度</p>

      {showToken && tokenData && (
        <div className={classNames(style.token)}>
          <p>额度包含:</p>
          <p>
            总赠送额度：{tokenData.dailyFreeCount}/{tokenData.dailyFreeCountTotal}
          </p>
          <p>
            套餐额度：{tokenData.packageCount}/{tokenData.packageCountTotal}
          </p>
        </div>
      )}
    </div>
  )

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: '300px',
      }}
      title={pdfProTip}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={styleCSS} />
    </Tooltip>
  )
})

//过期时间后的提示
export const ExpiredTip = observer((props: { styleCSS?: React.CSSProperties }) => {
  const { styleCSS = {} } = props
  const tip = (
    <div>
      <p>会译生效规则</p>
      <p>1.若您同时购买了年度会员和月度会员，会员时长会叠加</p>
      <p>2.若您同时购买了【连续包月】会员和其他会员，自动续费仍会按照每月去扣费，可随时取消</p>
      <p>3.若您同时购买了多个版本的会员，会优先应用更高级会员的会员周期</p>
    </div>
  )

  return (
    <Tooltip
      overlayStyle={{
        maxWidth: '300px',
      }}
      title={tip}
      placement={'bottom'}
      getPopupContainer={(el) => el.parentElement!}
    >
      <QuestionCircleOutlined className={style.questionIcon} style={styleCSS} />
    </Tooltip>
  )
})
